import store from '@/redux/store';
import { baseURL } from '@/config/APIConfig';

const MAX_MEMORY_LOGS = 50;
const SENSITIVE_KEYS = /^(authorization|password|token|secret|api[_-]?key)$/i;

const logs = [];
const ERROR_LOG_ENDPOINT = `${baseURL.AiBaseURL.replace(/\/$/, '')}/api/client-error-logs`;

// ─── helpers ───────────────────────────────────────────────────────────────

const safeStringify = (value, maxLength = 500) => {
    if (value == null) return null;
    if (typeof value === 'string') {
        return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
    }
    try {
        const str = JSON.stringify(value);
        return str.length > maxLength ? `${str.slice(0, maxLength)}…` : str;
    } catch {
        return String(value);
    }
};

const sanitizeObject = (obj, depth = 0) => {
    if (obj == null || depth > 3) return obj;
    if (typeof obj !== 'object') return obj;
    if (obj instanceof FormData) return '[FormData]';

    if (Array.isArray(obj)) {
        return obj.slice(0, 20).map((item) => sanitizeObject(item, depth + 1));
    }

    return Object.entries(obj).reduce((acc, [key, value]) => {
        if (SENSITIVE_KEYS.test(key)) {
            acc[key] = '[REDACTED]';
        } else {
            acc[key] = sanitizeObject(value, depth + 1);
        }
        return acc;
    }, {});
};

const extractServerMessage = (error) => {
    const data = error?.response?.data;
    if (!data) return error?.message || 'Unknown error';
    if (typeof data === 'string') return data;
    return data.message || data.error || data.title || safeStringify(data);
};

const getTodayDate = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const getCurrentLogUser = () => {
    let user = store.getState()?.auth?.userDetails;
    if (Array.isArray(user)) user = user[0];

    if (!user) {
        try {
            const stored = localStorage.getItem('authUser');
            const parsed = stored ? JSON.parse(stored) : null;
            user = Array.isArray(parsed) ? parsed[0] : parsed;
        } catch {
            user = null;
        }
    }

    return {
        userId: String(user?.id ?? user?.userId ?? 'guest'),
        userName: user?.username ?? user?.userName ?? user?.email ?? 'guest',
        companyId: String(user?.companyId ?? 'unknown'),
    };
};

export const buildApiErrorLogEntry = (error, meta = {}) => {
    const config = error?.config || {};
    const response = error?.response;
    const status = response?.status ?? null;
    const isNetworkError = !response;
    const isTimeout =
        error?.code === 'ECONNABORTED' ||
        (typeof error?.message === 'string' && error.message.toLowerCase().includes('timeout'));
    const user = getCurrentLogUser();

    return {
        timestamp: new Date().toISOString(),
        date: getTodayDate(),
        userId: user.userId,
        userName: user.userName,
        companyId: user.companyId,
        source: meta.source || 'api',
        method: (config.method || meta.method || 'GET').toUpperCase(),
        url: config.url || meta.url || meta.endpoint || '',
        baseURL: config.baseURL || meta.baseURL || '',
        status,
        statusText: response?.statusText || null,
        message: extractServerMessage(error),
        code: error?.code || null,
        isNetworkError,
        isTimeout,
        responseData: sanitizeObject(response?.data),
        page: typeof window !== 'undefined' ? window.location.pathname : null,
        endpoint: meta.endpoint || null,
        metaData: sanitizeObject(meta),
    };
};

/** POST error to backend API (no local file / localStorage) */
const sendErrorToBackend = (entry) => {
    const token = localStorage.getItem('authToken');

    fetch(ERROR_LOG_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(entry),
        keepalive: true,
    }).catch(() => { });
};

/** Fetch logs from backend — filter by date / user */
export const fetchApiErrorLogsFromBackend = async ({
    date,
    userId,
    companyId,
    limit = 50,
} = {}) => {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (userId) params.set('userId', userId);
    if (companyId) params.set('companyId', companyId);
    params.set('limit', String(limit));

    const token = localStorage.getItem('authToken');
    const res = await fetch(`${ERROR_LOG_ENDPOINT}?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!res.ok) throw new Error(`Failed to fetch logs: ${res.status}`);
    return res.json();
};

export const logApiError = (error, meta = {}) => {
    if (!error || error.__apiErrorLogged) return null;

    const entry = buildApiErrorLogEntry(error, meta);
    error.__apiErrorLogged = true;

    logs.unshift(entry);
    if (logs.length > MAX_MEMORY_LOGS) logs.pop();

    sendErrorToBackend(entry);

    if (process.env.NODE_ENV !== 'production') {
        console.groupCollapsed(
            `[API Error] ${entry.method} ${entry.url} → ${entry.status ?? entry.code ?? 'failed'}`
        );
        console.error(entry);
        console.groupEnd();
    }

    return entry;
};

export const getApiErrorLogs = () => [...logs];

export const clearApiErrorLogs = () => {
    logs.length = 0;
};

export const attachAxiosErrorLogger = (axiosInstance, source, options = {}) => {
    axiosInstance.interceptors.response.use(
        (response) => response,
        (error) => {
            if (!options.skip?.(error)) {
                logApiError(error, { source });
            }
            return Promise.reject(error);
        }
    );
};

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    window.__shipezApiErrorLogs = {
        get: getApiErrorLogs,
        clear: clearApiErrorLogs,
        fetchFromBackend: fetchApiErrorLogsFromBackend,
    };
}
